import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';

const TABS = [
  { id: 'vehicle', label: 'Mi vehiculo' },
  { id: 'expenses', label: 'Bitacora' },
  { id: 'maintenance', label: 'Mantenimiento' },
  { id: 'parts', label: 'Repuestos' },
  { id: 'metrics', label: 'Metricas' },
];

const EXPENSE_CATEGORIES = ['combustible', 'mantenimiento', 'repuestos', 'seguro', 'otros'];

const CUALtaxiPanel = ({ user, onExit }) => {
  const [tab, setTab] = useState('vehicle');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({});
  const [expenses, setExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState({ total_cualcoin: 0, by_category: {} });
  const [maintenance, setMaintenance] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [parts, setParts] = useState([]);
  const [metrics, setMetrics] = useState(null);

  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: 'combustible', amount_cualcoin: '', description: '', odometer_km: '' });
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [maintForm, setMaintForm] = useState({ item: '', odometer_km: '', cost_cualcoin: '', next_due_km: '', notes: '' });

  const fetchStatus = useCallback(async () => {
    const r = await axios.get(`${API}/cualtaxi/status`);
    setStatus(r.data);
    setVehicle(r.data.vehicle || null);
    if (r.data.vehicle) setVehicleForm(r.data.vehicle);
  }, []);

  const fetchExpenses = useCallback(async () => {
    const r = await axios.get(`${API}/cualtaxi/expenses?limit=200`);
    setExpenses(r.data.expenses || []);
    setExpenseSummary(r.data.summary || { total_cualcoin: 0, by_category: {} });
  }, []);

  const fetchMaintenance = useCallback(async () => {
    const r = await axios.get(`${API}/cualtaxi/maintenance`);
    setMaintenance(r.data.maintenance || []);
    setAlerts(r.data.alerts || []);
  }, []);

  const fetchParts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cualtaxi/marketplace/specialized?limit=60`);
      setParts(r.data.listings || []);
    } catch { setParts([]); }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cualtaxi/metrics`);
      setMetrics(r.data);
    } catch { setMetrics(null); }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchStatus();
      setLoading(false);
    })();
  }, [fetchStatus]);

  useEffect(() => {
    if (tab === 'expenses') fetchExpenses();
    if (tab === 'maintenance') fetchMaintenance();
    if (tab === 'parts') fetchParts();
    if (tab === 'metrics') fetchMetrics();
  }, [tab, fetchExpenses, fetchMaintenance, fetchParts, fetchMetrics]);

  const saveVehicle = async () => {
    const payload = { ...vehicleForm };
    ['year', 'odometer_km', 'seats'].forEach(k => { if (payload[k] !== undefined && payload[k] !== '') payload[k] = parseInt(payload[k]); });
    if (payload.fuel_consumption_kml !== undefined && payload.fuel_consumption_kml !== '') payload.fuel_consumption_kml = parseFloat(payload.fuel_consumption_kml);
    try {
      await axios.put(`${API}/cualtaxi/vehicle`, payload);
      await fetchStatus();
      alert('Vehiculo guardado');
    } catch (e) { alert(e.response?.data?.detail || 'Error guardando'); }
  };

  const submitExpense = async () => {
    if (!expenseForm.amount_cualcoin || !expenseForm.description) return;
    try {
      await axios.post(`${API}/cualtaxi/expenses`, {
        category: expenseForm.category,
        amount_cualcoin: parseFloat(expenseForm.amount_cualcoin),
        description: expenseForm.description,
        odometer_km: expenseForm.odometer_km ? parseInt(expenseForm.odometer_km) : null,
      });
      setExpenseForm({ category: 'combustible', amount_cualcoin: '', description: '', odometer_km: '' });
      setShowExpenseForm(false);
      fetchExpenses();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const submitMaintenance = async () => {
    if (!maintForm.item || !maintForm.odometer_km) return;
    try {
      await axios.post(`${API}/cualtaxi/maintenance`, {
        item: maintForm.item,
        odometer_km: parseInt(maintForm.odometer_km),
        cost_cualcoin: parseFloat(maintForm.cost_cualcoin || 0),
        next_due_km: maintForm.next_due_km ? parseInt(maintForm.next_due_km) : null,
        notes: maintForm.notes || null,
      });
      setMaintForm({ item: '', odometer_km: '', cost_cualcoin: '', next_due_km: '', notes: '' });
      setShowMaintForm(false);
      fetchMaintenance();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4" data-testid="cualtaxi-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-white/80">Modo profesional</p>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            🚖 CUALtaxi
          </h2>
          <p className="text-xs text-white/90 mt-1">
            Activado: {status?.activated_at ? new Date(status.activated_at).toLocaleDateString('es-VE') : '—'}
          </p>
        </div>
        <button
          onClick={onExit}
          data-testid="cualtaxi-exit"
          className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm"
        >
          ← Volver a Transporte
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`cualtaxi-tab-${t.id}`}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.id ? 'border-orange-500 text-orange-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {/* VEHICLE */}
      {tab === 'vehicle' && (
        <div className="bg-white rounded-xl p-5 border border-gray-200 space-y-3" data-testid="vehicle-form">
          <h3 className="font-semibold text-gray-800">Ficha del vehiculo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Field label="Marca" v={vehicleForm.brand} on={(v) => setVehicleForm({...vehicleForm, brand: v})} testid="v-brand" />
            <Field label="Modelo" v={vehicleForm.model} on={(v) => setVehicleForm({...vehicleForm, model: v})} testid="v-model" />
            <Field label="Año" type="number" v={vehicleForm.year} on={(v) => setVehicleForm({...vehicleForm, year: v})} testid="v-year" />
            <Field label="Placa" v={vehicleForm.plate} on={(v) => setVehicleForm({...vehicleForm, plate: v})} testid="v-plate" />
            <Field label="Color" v={vehicleForm.color} on={(v) => setVehicleForm({...vehicleForm, color: v})} testid="v-color" />
            <Field label="Asientos" type="number" v={vehicleForm.seats} on={(v) => setVehicleForm({...vehicleForm, seats: v})} testid="v-seats" />
            <Field label="Consumo (km/litro)" type="number" v={vehicleForm.fuel_consumption_kml} on={(v) => setVehicleForm({...vehicleForm, fuel_consumption_kml: v})} testid="v-consumption" />
            <Field label="Kilometraje actual" type="number" v={vehicleForm.odometer_km} on={(v) => setVehicleForm({...vehicleForm, odometer_km: v})} testid="v-odometer" />
          </div>
          <div className="flex justify-end">
            <button onClick={saveVehicle} data-testid="vehicle-save" className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium">
              Guardar
            </button>
          </div>
        </div>
      )}

      {/* EXPENSES */}
      {tab === 'expenses' && (
        <div className="space-y-3" data-testid="expenses-panel">
          <div className="bg-white rounded-xl p-4 border border-gray-200 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            <div className="col-span-2 md:col-span-1">
              <p className="text-gray-500">Total</p>
              <p className="font-bold text-lg text-gray-800">{expenseSummary.total_cualcoin.toFixed(2)} Cc</p>
            </div>
            {EXPENSE_CATEGORIES.map(c => (
              <div key={c}>
                <p className="text-gray-500 capitalize">{c}</p>
                <p className="font-semibold text-gray-700">{(expenseSummary.by_category[c] || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button onClick={() => setShowExpenseForm(v => !v)} data-testid="add-expense-btn" className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">
              + Registrar gasto
            </button>
          </div>

          {showExpenseForm && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <select value={expenseForm.category} onChange={(e) => setExpenseForm({...expenseForm, category: e.target.value})} data-testid="expense-category" className="px-3 py-2 border rounded text-sm">
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="number" step="0.01" placeholder="Monto en Cc" value={expenseForm.amount_cualcoin} onChange={(e) => setExpenseForm({...expenseForm, amount_cualcoin: e.target.value})} data-testid="expense-amount" className="px-3 py-2 border rounded text-sm" />
                <input placeholder="Descripcion" value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} data-testid="expense-desc" className="px-3 py-2 border rounded text-sm md:col-span-2" />
                <input type="number" placeholder="Km al momento (opcional)" value={expenseForm.odometer_km} onChange={(e) => setExpenseForm({...expenseForm, odometer_km: e.target.value})} className="px-3 py-2 border rounded text-sm md:col-span-2" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowExpenseForm(false)} className="px-3 py-1.5 text-sm">Cancelar</button>
                <button onClick={submitExpense} data-testid="expense-submit" className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm">Guardar</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 divide-y">
            {expenses.length === 0 && <p className="p-4 text-sm text-gray-500 text-center">Sin gastos registrados</p>}
            {expenses.map(e => (
              <div key={e.id} className="p-3 flex justify-between items-center text-sm" data-testid={`expense-${e.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">{e.description}</p>
                  <p className="text-xs text-gray-500 capitalize">{e.category} · {new Date(e.occurred_at).toLocaleDateString('es-VE')}{e.odometer_km ? ` · ${e.odometer_km} km` : ''}</p>
                </div>
                <p className="font-bold text-gray-800 ml-2">{e.amount_cualcoin.toFixed(2)} Cc</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MAINTENANCE */}
      {tab === 'maintenance' && (
        <div className="space-y-3" data-testid="maintenance-panel">
          {alerts.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3" data-testid="maint-alerts">
              <p className="font-semibold text-amber-800 text-sm mb-1">Alertas de mantenimiento</p>
              {alerts.map((a, i) => (
                <p key={i} className="text-xs text-amber-700">• {a.item}: {a.remaining_km > 0 ? `faltan ${a.remaining_km} km` : `vencido hace ${-a.remaining_km} km`}</p>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <button onClick={() => setShowMaintForm(v => !v)} data-testid="add-maint-btn" className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">
              + Registrar mantenimiento
            </button>
          </div>
          {showMaintForm && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <input placeholder="Item (ej: Cambio de aceite)" value={maintForm.item} onChange={(e) => setMaintForm({...maintForm, item: e.target.value})} data-testid="maint-item" className="px-3 py-2 border rounded md:col-span-2" />
              <input type="number" placeholder="Km al momento" value={maintForm.odometer_km} onChange={(e) => setMaintForm({...maintForm, odometer_km: e.target.value})} data-testid="maint-km" className="px-3 py-2 border rounded" />
              <input type="number" step="0.01" placeholder="Costo Cc" value={maintForm.cost_cualcoin} onChange={(e) => setMaintForm({...maintForm, cost_cualcoin: e.target.value})} data-testid="maint-cost" className="px-3 py-2 border rounded" />
              <input type="number" placeholder="Proximo a los X km (opcional)" value={maintForm.next_due_km} onChange={(e) => setMaintForm({...maintForm, next_due_km: e.target.value})} className="px-3 py-2 border rounded md:col-span-2" />
              <textarea placeholder="Notas" value={maintForm.notes} onChange={(e) => setMaintForm({...maintForm, notes: e.target.value})} className="px-3 py-2 border rounded md:col-span-2" rows={2} />
              <div className="md:col-span-2 flex justify-end gap-2">
                <button onClick={() => setShowMaintForm(false)} className="px-3 py-1.5">Cancelar</button>
                <button onClick={submitMaintenance} data-testid="maint-submit" className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded">Guardar</button>
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 divide-y">
            {maintenance.length === 0 && <p className="p-4 text-sm text-gray-500 text-center">Sin registros</p>}
            {maintenance.map(m => (
              <div key={m.id} className="p-3 text-sm">
                <div className="flex justify-between">
                  <p className="font-medium">{m.item}</p>
                  <p className="text-gray-700">{m.cost_cualcoin?.toFixed(2)} Cc</p>
                </div>
                <p className="text-xs text-gray-500">{m.odometer_km} km{m.next_due_km ? ` · proximo: ${m.next_due_km} km` : ''} · {new Date(m.performed_at).toLocaleDateString('es-VE')}</p>
                {m.notes && <p className="text-xs text-gray-600 italic mt-1">{m.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PARTS */}
      {tab === 'parts' && (
        <div className="space-y-2" data-testid="parts-panel">
          <p className="text-xs text-gray-500">Marketplace especializado de repuestos y servicios CUALtaxi (no se mezcla con la categoria general "Vehiculos" del marketplace).</p>
          {parts.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-6 text-center text-sm text-gray-500">
              Aun no hay anuncios especializados. Los proveedores deben taggear sus listings con "cualtaxi" en el marketplace para aparecer aqui.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {parts.map(p => (
                <div key={p.id} className="bg-white rounded-xl border p-3 text-sm">
                  <p className="font-semibold text-gray-800">{p.title}</p>
                  <p className="text-xs text-gray-500 truncate">{p.description}</p>
                  <p className="text-amber-700 font-bold mt-2">{p.price_cualcoin?.toFixed(2)} Cc</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* METRICS */}
      {tab === 'metrics' && metrics && (
        <div className="space-y-3" data-testid="metrics-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Stat label="Gasto total" v={`${metrics.total_spend_cualcoin.toFixed(2)} Cc`} />
            <Stat label="Costo combustible / km" v={metrics.cost_per_km_fuel_cualcoin !== null ? `${metrics.cost_per_km_fuel_cualcoin} Cc` : '—'} hint="estimado" />
            <Stat label="Viajes registrados" v={metrics.trips_count} />
          </div>
          {metrics.vehicle_summary && (
            <div className="bg-white rounded-xl p-4 border text-sm">
              <p className="font-semibold text-gray-800">{metrics.vehicle_summary.brand} {metrics.vehicle_summary.model}</p>
              <p className="text-xs text-gray-500">Kilometraje actual: {metrics.vehicle_summary.odometer_km ?? '—'} km</p>
            </div>
          )}
          <p className="text-[10px] text-gray-400 italic">Las metricas son informativas, no prescriptivas. Las situaciones reales (trafico, espera, ruta) hacen fluctuar el costo real por viaje.</p>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, v, on, type = 'text', testid }) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <input type={type} value={v ?? ''} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full mt-0.5 px-3 py-2 border border-gray-300 rounded text-sm" />
  </div>
);

const Stat = ({ label, v, hint }) => (
  <div className="bg-white rounded-xl border p-4">
    <p className="text-xs text-gray-500">{label} {hint && <span className="text-[10px] italic">({hint})</span>}</p>
    <p className="text-xl font-bold text-gray-800">{v}</p>
  </div>
);

export default CUALtaxiPanel;
